import { useEffect, useRef, useState } from 'react'

import { getChatroomMessages } from '../../../../api'
import { CHATROOM_MESSAGES_LIMIT } from '../../../../constants/chatroom'
import { EMPTY_LIST } from '../../../../constants/common'
import { useApi } from '../../../../hooks'
import {
  applyChatroomMessageUpdate,
  getChatroomMessagesFromGetPrevResponse,
} from '../../Chatroom.utils'

export function useChatroomMessagesList({ outboundUuid, uuid }) {
  const [messages, setMessages] = useState(EMPTY_LIST)
  const [isLoadingInitial, setLoadingInitial] = useState(Boolean(uuid))
  const [isLoadingOlder, setLoadingOlder] = useState(false)
  const [hasMoreOlder, setHasMoreOlder] = useState(Boolean(uuid))

  const latestMessageUuidRef = useRef(null)
  const messagesRef = useRef(EMPTY_LIST)

  const initialApi = useApi()
  const olderApi = useApi()
  const refetchVisibleApi = useApi()

  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- resets the message list when the chatroom changes
    setMessages(EMPTY_LIST)
    latestMessageUuidRef.current = null

    if (uuid) {
      setHasMoreOlder(true)
      setLoadingInitial(true)

      initialApi
        .call(getChatroomMessages, { getPrev: true, outboundUuid, uuid })
        .then(response => {
          if (response) {
            const loaded = getChatroomMessagesFromGetPrevResponse(response)

            latestMessageUuidRef.current = loaded.at(-1)?.uuid || null
            setMessages(loaded)

            if (loaded.length < CHATROOM_MESSAGES_LIMIT) {
              setHasMoreOlder(false)
            }
          }
        })
        .finally(() => {
          setLoadingInitial(false)
        })

      return () => {
        initialApi.abort()
      }
    }

    setHasMoreOlder(false)
    setLoadingInitial(false)
  }, [initialApi, outboundUuid, uuid])

  function handleLoadOlder(oldestUuid) {
    if (uuid && oldestUuid) {
      setLoadingOlder(true)

      olderApi
        .call(getChatroomMessages, {
          from: oldestUuid,
          getPrev: true,
          outboundUuid,
          uuid,
        })
        .then(response => {
          if (response) {
            const older = getChatroomMessagesFromGetPrevResponse(response)

            if (older.length > 0) {
              setMessages(prev => [...older, ...prev])
            }

            if (older.length < CHATROOM_MESSAGES_LIMIT) {
              setHasMoreOlder(false)
            }
          }
        })
        .finally(() => {
          setLoadingOlder(false)
        })
    }
  }

  function handleAppendMessage(message) {
    if (message?.uuid) {
      setMessages(prev =>
        prev.some(item => item?.uuid === message.uuid)
          ? prev
          : [...prev, message]
      )
      latestMessageUuidRef.current = message.uuid
    }
  }

  function handleAppendMessages(freshMessages) {
    if (freshMessages.length > 0) {
      setMessages(prev => [...prev, ...freshMessages])
      latestMessageUuidRef.current = freshMessages.at(-1).uuid
    }
  }

  function handleUpdateMessage(messageUuid, partial) {
    setMessages(prev =>
      prev.map(message =>
        message.uuid === messageUuid
          ? applyChatroomMessageUpdate({ message, partial })
          : message
      )
    )
  }

  function handleRefetchVisibleMessages() {
    if (uuid) {
      refetchVisibleApi
        .call(getChatroomMessages, { getPrev: true, outboundUuid, uuid })
        .then(response => {
          if (response) {
            const latest = getChatroomMessagesFromGetPrevResponse(response)

            if (latest.length > 0) {
              const latestUuids = new Set(latest.map(message => message?.uuid))

              setMessages(prev => [
                ...prev.filter(message => !latestUuids.has(message?.uuid)),
                ...latest,
              ])

              latestMessageUuidRef.current = latest.at(-1).uuid
            }
          }
        })
    }
  }

  return {
    handleAppendMessage,
    handleAppendMessages,
    handleLoadOlder,
    handleRefetchVisibleMessages,
    handleUpdateMessage,
    hasMoreOlder,
    isLoadingInitial,
    isLoadingOlder,
    latestMessageUuidRef,
    messages,
    messagesRef,
  }
}
